import {
  elementaryV2ContentfulMappings,
  middleSchoolV2ContentfulMappings,
  digitalWellBeingV2ContentfulMappings,
} from 'utils/product/contentfulMappings'
import {
  programKeyMap,
  GRADE_K2_TIERED_INTERVENTIONS,
  GRADE_35_TIERED_INTERVENTIONS,
  GRADE_68_TIERED_INTERVENTIONS,
} from 'utils/program/types'
import {
  isK5Url,
  isK5Dashboard,
  isMiddleSchoolUrl,
  isMiddleSchoolDashboard,
} from 'utils/routing/routeHelpers'
import { getSlug, extractClassIdFromSearch } from 'utils/routing/urlHelpers'

export function isCurrentSiteMiddleSchool(v2Payload, userContextManager) {
  if (!userContextManager?.middleSchoolActiveSiteId) {
    // don't filter at all if there's no active site
    return true
  }
  return v2Payload?.site_id === userContextManager?.middleSchoolActiveSiteId
}

const CONVERSION_PROGRAM_TO_PRODUCT = {
  MSP: 'middle-school',
  K5P: 'elementary',
}

/**
 * Extracts a bare grade key (k, 0-12) from known curriculum-service / LMS
 * course code patterns. Returns null when no grade can be inferred.
 */
export const extractGradeKeyFromCourseCode = courseCode => {
  if (typeof courseCode !== 'string') return null

  const normalized = courseCode.trim().toLowerCase()
  if (!normalized) return null

  const GRADE_KEY_PATTERN = '(k|\\d{1,2})'

  const COURSE_CODE_GRADE_PATTERNS = [
    new RegExp(`g${GRADE_KEY_PATTERN}`),
    new RegExp(`^${GRADE_KEY_PATTERN}_dwb_`),
    new RegExp(`^${GRADE_KEY_PATTERN}_core_tier-1_siteeducator$`),
  ]
  for (const pattern of COURSE_CODE_GRADE_PATTERNS) {
    const gradeKey = normalized.match(pattern)?.[1]
    if (gradeKey) return gradeKey
  }

  return null
}

/**
 * Normalizes a router grade URL segment (e.g. "grade-5") to a bare grade key.
 */
export const normalizeGradeUrlSegment = gradeSegment => {
  if (typeof gradeSegment !== 'string' || !gradeSegment.startsWith('grade-')) {
    return null
  }

  return gradeSegment.replace('grade-', '').toLowerCase()
}

/**
 * Builds the set of LMS registration `course_name` values that should match
 * a teach route's grade segment and resolved curriculum course code.
 */
export const buildRegistrationCourseNameCandidates = (
  gradeSegment,
  courseCode,
) => {
  return new Set(
    [
      normalizeGradeUrlSegment(gradeSegment),
      extractGradeKeyFromCourseCode(courseCode),
    ]
      .filter(Boolean)
      .map(value => value.toLowerCase()),
  )
}

/**
 * Normalizes an LMS course_name for course-tree and contentful mapping lookups.
 * Extracts a bare grade key from known LMS code patterns (e.g. "7_core_tier-1_siteEducator" -> "7");
 * otherwise returns the trimmed, lowercased course_name unchanged.
 */
export function normalizeLmsCourseName(courseName) {
  const normalized = String(courseName || '')
    .trim()
    .toLowerCase()
  if (!normalized) return ''

  const extractedGrade = extractGradeKeyFromCourseCode(normalized)
  if (extractedGrade) return extractedGrade

  return normalized
}

export function getLmsCourseTreeLookupKeys(v2Payload, program = 'MSP') {
  const rawCourseName = v2Payload?.course_name
  const candidates = []
  const addCandidate = candidate => {
    if (!candidate || candidates.includes(candidate)) return
    candidates.push(candidate)
  }

  addCandidate(rawCourseName)

  const normalizedGrade = normalizeLmsCourseName(rawCourseName)
  if (!normalizedGrade) return candidates

  addCandidate(normalizedGrade)
  addCandidate(normalizedGrade === 'k' ? 'K' : normalizedGrade)
  if (!normalizedGrade.startsWith('grade-')) {
    addCandidate(`grade-${normalizedGrade}`)
  }

  const productProgram =
    CONVERSION_PROGRAM_TO_PRODUCT[program] ?? program ?? 'middle-school'
  if (productProgram === 'digital-well-being') {
    addCandidate(`${normalizedGrade}_dwb_tier-1_siteEducator`)
  }

  return candidates
}

function getCourseNameInContext(v2Payload) {
  if (!v2Payload || !v2Payload?.course_name) {
    return null
  }
  const normalizedGrade = normalizeLmsCourseName(v2Payload.course_name)
  if (normalizedGrade === 'k') {
    return 'K'
  }
  if (normalizedGrade) {
    return normalizedGrade
  }
  return v2Payload?.course_name
}

export function resolveCourseTreeFromLookupKeys({ courseTrees, lookupKeys }) {
  if (!courseTrees || !lookupKeys?.length) return null

  for (const lookupKey of lookupKeys) {
    const courseTree = courseTrees?.[lookupKey]
    if (courseTree) return courseTree
  }

  return null
}

export function countTotalLessons(v2Payload, courseManager, lookupKeys) {
  const courses = courseManager?.courseTrees
  if (!v2Payload || !v2Payload?.course_name || !courses) {
    return 0
  }

  const courseTree =
    resolveCourseTreeFromLookupKeys({
      courseTrees: courses,
      lookupKeys,
    }) ?? courses?.[getCourseNameInContext(v2Payload)]

  if (!courseTree) {
    return 0
  }

  // Count required lessons in the tree
  return countLessonsInNode(courseTree)
}

// Helper function to recursively count lessons in a course node
export function countLessonsInNode(node) {
  if (!node) {
    return 0
  }

  // If this is a leaf lesson node, it counts as 1
  if (node?.type === 'lesson' && node?.requirementType !== 'choice') {
    return 1
  }

  // If no children, return 0
  if (!node?.children || node?.children?.length === 0) {
    return 0
  }

  // Special case for choiceCurriculum - only count required number (typically 1)
  if (node?.type === 'choiceCurriculum' && node?.childrenRequired) {
    return node?.childrenRequired
  }

  // Recursively count lessons in children
  return (
    node?.children?.reduce((sum, childNode) => {
      return sum + countLessonsInNode(childNode)
    }, 0) || 0
  )
}

/**
 * Extracts classes from instructor data based on program type
 *
 * This function takes instructor data and filters/converts classes based on the program type
 * determined from the URL parameters. It supports both Elementary (K-5) and Middle School (6-8)
 * programs.
 *
 * @param {Object} instructorData - The raw instructor data containing classes for different programs
 * @param {Object} courseManager - The course manager containing course information
 * @param {Object} match - The router match object containing URL parameters
 * @param {Object} userContext - The user context object for site filtering
 * @returns {Array} - Filtered and converted classes for the appropriate program
 */
export const selectCurrentProgramClasses = ({
  instructorData,
  courseManager,
  match,
  userContext,
}) => {
  if (!instructorData) return []

  // TODO LEARN-23339: DOUBLE CHECK THESE ARE ENOUGH CHECKS
  const isElemProgram = isK5Url() || isK5Dashboard()
  const isMsProgram = isMiddleSchoolUrl() || isMiddleSchoolDashboard()

  // Elementary (K-5) program
  if (isElemProgram) {
    if (instructorData?.K5SelDigital?.siteEducator) {
      return instructorData.K5SelDigital.siteEducator
        .filter(course => isCurrentSiteElementary(course, userContext))
        .map(course =>
          convertLmsV2ToLmsV1Dot5PayloadShape(course, courseManager, 'K5P'),
        )
        .filter(course => course && !(course.tier === 2 && course.is_finished))
    }
    // Fallback to v1 elementary classes
    return instructorData?.Elementary || instructorData?.Classes || []
  }

  // Middle School (6-8) program
  if (isMsProgram) {
    if (instructorData?.MspDigital?.siteEducator) {
      return instructorData.MspDigital.siteEducator
        .filter(course => isCurrentSiteMiddleSchool(course, userContext))
        .map(course =>
          convertLmsV2ToLmsV1Dot5PayloadShape(course, courseManager, 'MSP'),
        )
        .filter(course => course && !(course.tier === 2 && course.is_finished))
    }
    // Fallback to v1 middle school classes
    return instructorData?.Classes || []
  }

  // Non-K8 routes - fallback to v1 classes
  console.warn('no case matched. Returning v1 classes')
  return instructorData?.Classes || []
}

/**
 * Converts LMS v2 class data to LMS v1 class format. We should remove this once we decide to update to using just v2 data shapes in Lessons and k8teach pages
 * NOTE: since the v2 system only adds lessons if they are attempted we have to add fake lessons (and units) from the course tree. We need to work with PIT to come up with a more sustainable data model
 * @param {Object} v2Class - v2 class object
 * @param {Object} courseTree - The program flex course tree structure
 * @returns {Object} v1 class object
 */
export const convertLmsv2ClassesToLmsv1Classes = (v2Class, courseTree) => {
  if (!v2Class || !courseTree) return null

  // Create base v1 class structure
  const v1Class = {
    licenseId: null, // Not present in v2
    siteId: v2Class.site_id,
    learner: 'Placeholder user name',
    instance: String(v2Class.registration_id),
    sco: 'Placeholder scoId',
    scoID: 'Placeholder scoId',
    is_done: null, // Will be set after processing units
    attempted: 'Attempted placeholder',
    progress: null,
    score: null,
    doneAt: null,
    updatedAt: null,
    time: 'P0D',
    totalLessons: 0,
    completeLessons: 0,
    contentfulID: courseTree.contentfulId,
    title: v2Class.class_title || `Grade ${v2Class.course_name?.toUpperCase()}`,
    createdDate: v2Class.created_at,
    user_id: v2Class.user_id,
    secondary_user_ids: v2Class.secondary_user_ids || [],
    units: [],
  }

  // Process units from course tree
  const units = courseTree.children.filter(node => node.type === 'unit')

  units.forEach(unitNode => {
    const v1Unit = {
      scoID: 'Placeholder scoId',
      contentfulID: unitNode.contentfulId,
      learner: 'Placeholder user name',
      instance: String(v2Class.registration_id),
      sco: 'Placeholder scoId',
      is_done: null, // Will be set after processing lessons and choiceCurriculum
      attempted: 'Attempted placeholder',
      progress: null,
      score: null,
      doneAt: null,
      updatedAt: null,
      time: 'P0D',
      totalLessons: 0,
      completeLessons: 0,
      lessons: [],
    }

    // Process all children in unit (lessons and choiceCurriculum nodes)
    let allRequiredItemsDone = true

    unitNode.children.forEach(node => {
      if (node.type === 'lesson') {
        // Process regular lesson
        const v2Lesson = v2Class.lesson_nodes?.find(
          l => l.leaf_node_id === node.leafNodeId,
        )

        let attempted = 'not attempted'
        if (v2Lesson && v2Lesson?.is_done) {
          attempted = 'completed'
        } else if (v2Lesson && v2Lesson?.progress !== null) {
          attempted = 'incomplete'
        }

        const v1Lesson = {
          scoID: 'Placeholder scoId',
          contentfulID: node.contentfulId,
          learner: 'Placeholder user name',
          instance: String(v2Class.registration_id),
          v2_class_registration_id: String(v2Class.registration_id),
          sco: 'Placeholder scoId',
          is_done: v2Lesson?.is_done ?? false,
          attempted: attempted,
          progress: v2Lesson?.progress ?? null,
          score: null,
          doneAt: v2Lesson?.done_at || null,
          updatedAt: v2Lesson?.updated_at || null,
          time: 'P0D',
          leaf_node_id: node.leafNodeId,
          attempt_id: v2Lesson?.attempt_id || null,
          is_passed: v2Lesson?.is_passed || false,
          lesson_interaction_type: v2Lesson?.lesson_interaction_type || null,
          v1_lesson_registration_id:
            v2Lesson?.v1_lesson_registration_id || null,
          type: 'lesson', // Add type to distinguish from choiceCurriculum
        }

        v1Unit.lessons.push(v1Lesson)
        v1Unit.totalLessons++

        if (v2Lesson?.is_done) {
          v1Unit.completeLessons++
        } else {
          allRequiredItemsDone = false
        }
      } else if (node.type === 'choiceCurriculum') {
        // Process choiceCurriculum node
        const childLessons = []
        let choiceCompleted = false
        let choiceAttempted = 'not attempted'
        let choiceDoneAt = null
        let choiceUpdatedAt = null

        // Process child lessons of choiceCurriculum
        node.children.forEach(childLesson => {
          const v2ChildLesson = v2Class.lesson_nodes?.find(
            l => l.leaf_node_id === childLesson.leafNodeId,
          )

          let childAttempted = 'not attempted'
          if (v2ChildLesson && v2ChildLesson?.is_done) {
            childAttempted = 'completed'
            choiceCompleted = true // If any child is complete, choiceCurriculum is complete
            choiceDoneAt = v2ChildLesson?.done_at || choiceDoneAt
            choiceUpdatedAt = v2ChildLesson?.updated_at || choiceUpdatedAt
          } else if (v2ChildLesson && v2ChildLesson?.progress !== null) {
            childAttempted = 'incomplete'
          }

          // Update choiceCurriculum attempted status
          if (
            childAttempted !== 'not attempted' &&
            choiceAttempted === 'not attempted'
          ) {
            choiceAttempted = childAttempted
          }
          if (childAttempted === 'completed') {
            choiceAttempted = 'completed'
          }

          const v1ChildLesson = {
            scoID: 'Placeholder scoId',
            contentfulID: childLesson.contentfulId,
            learner: 'Placeholder user name',
            instance: String(v2Class.registration_id),
            v2_class_registration_id: String(v2Class.registration_id),
            sco: 'Placeholder scoId',
            is_done: v2ChildLesson?.is_done ?? false,
            attempted: childAttempted,
            progress: v2ChildLesson?.progress ?? null,
            score: null,
            doneAt: v2ChildLesson?.done_at || null,
            updatedAt: v2ChildLesson?.updated_at || null,
            time: 'P0D',
            leaf_node_id: childLesson.leafNodeId,
            attempt_id: v2ChildLesson?.attempt_id || null,
            is_passed: v2ChildLesson?.is_passed || false,
            lesson_interaction_type:
              v2ChildLesson?.lesson_interaction_type || null,
            v1_lesson_registration_id:
              v2ChildLesson?.v1_lesson_registration_id || null,
            type: 'lesson',
          }

          childLessons.push(v1ChildLesson)
        })

        // Create choiceCurriculum object
        const v1ChoiceCurriculum = {
          scoID: 'Placeholder scoId',
          contentfulID: node.contentfulId,
          learner: 'Placeholder user name',
          instance: String(v2Class.registration_id),
          v2_class_registration_id: String(v2Class.registration_id),
          sco: 'Placeholder scoId',
          is_done: choiceCompleted, // True if ANY child lesson is complete
          attempted: choiceAttempted,
          progress: null, // ChoiceCurriculum doesn't have individual progress
          score: null,
          doneAt: choiceDoneAt,
          updatedAt: choiceUpdatedAt,
          time: 'P0D',
          type: 'choiceCurriculum', // Distinguish from regular lessons
          childrenRequired: node.childrenRequired || 1, // How many choices are required
          choices: childLessons, // Array of child lesson options
        }

        v1Unit.lessons.push(v1ChoiceCurriculum)
        v1Unit.totalLessons++ // Count choiceCurriculum as 1 lesson

        if (choiceCompleted) {
          v1Unit.completeLessons++
        } else {
          allRequiredItemsDone = false
        }
      }
    })

    v1Unit.is_done = allRequiredItemsDone && v1Unit.totalLessons > 0 // Unit is done if all required items are done
    v1Class.units.push(v1Unit)
    v1Class.totalLessons += v1Unit.totalLessons
    v1Class.completeLessons += v1Unit.completeLessons
  })

  // Set class is_done based on all units being done
  v1Class.is_done =
    v1Class.units.length > 0 && v1Class.units.every(unit => unit.is_done)

  return v1Class
}

/**
 * Selects and transforms instructor class data based on the current route and program context.
 *
 * This function determines which classes to display by:
 * 1. Parsing the grade and program from the current route
 * 2. Applying program-specific filtering (elementary vs middle school)
 * 3. Converting LMS v2 class data to LMS v1 format when appropriate
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.instructorData - The instructor's data containing class information
 * @param {Object} params.instructorData.Classes - Legacy v1 classes array (fallback)
 * @param {Object} params.instructorData.K5SelDigital - Elementary program data
 * @param {Object} params.instructorData.K5SelDigital.siteEducator - Elementary classes for the current site
 * @param {Object} params.instructorData[program] - Program-specific data (dynamic key)
 * @param {Object} params.courseManager - Course management data containing course trees
 * @param {Object} params.courseManager.courseTrees - Course tree data indexed by grade
 * @param {Object} params.match - React Router match object containing URL parameters
 * @param {string} params.match.url - The current URL used to parse grade and program
 * @param {Object} params.userContext - User context containing site and program information
 *
 * @returns {Array} An array of class objects, either legacy v1 format or converted from v2
 *
 */
export const selectAllClassesInCurrentCourseData = ({
  instructorData,
  courseManager,
  match,
  userContext,
}) => {
  try {
    if (!instructorData) return []

    // Default to the legacy Classes array
    let classes = instructorData?.Classes || []
    let programClasses = null
    let isTier2 = null

    const { grade, program } = parseGradeAndProgram(match?.url) || {}
    isTier2 = typeof grade === 'string' && grade?.includes('tier2')

    const programFilter =
      program === programKeyMap?.['elementary'] && instructorData?.siteEducator
        ? isCurrentSiteElementary
        : isCurrentSiteMiddleSchool

    const modifiedGrade = grade === 'k' ? 'K' : grade
    const courseTree = courseManager?.courseTrees?.[modifiedGrade]

    programClasses = instructorData?.siteEducator

    if (programFilter && courseTree && programClasses) {
      classes = programClasses.filter(course =>
        programFilter(course, userContext),
      )

      // backwards compatibility for v1 lms usage in K8teach for now. Tier2 is all in v2 so no need to convert to a v1 shape
      if (!isTier2) {
        classes = classes.map(course =>
          convertLmsv2ClassesToLmsv1Classes(course, courseTree),
        )
      }
    }

    return classes
  } catch (e) {
    console.error('Error selecting current class instructor data', e)
    return []
  }
}

/**
 * Counts the number of completed lessons in a course
 *
 * @param {Object} v2Payload - The LMS v2 course payload
 * @returns {number} - The count of completed lessons
 */
export function countCompleteLessons(v2Payload) {
  if (!v2Payload || !v2Payload.lesson_nodes) {
    return 0
  }

  return v2Payload.lesson_nodes.reduce((sumCompleted, lesson) => {
    return lesson?.is_done ? sumCompleted + 1 : sumCompleted
  }, 0)
}

// NOTE: this leaves out many of the LMS v1.5 properties, it's only usable on the dashboard
export function convertLmsV2ToLmsV1Dot5PayloadShape(
  v2Payload,
  courseManager,
  program = 'MSP',
) {
  if (!v2Payload) {
    return null
  }

  const programContentfulMapping =
    program === 'K5P'
      ? elementaryV2ContentfulMappings
      : middleSchoolV2ContentfulMappings // default to middle school if no program provided

  // Normalize is_finished to handle string/number/boolean values from API
  const isFinished =
    v2Payload?.is_finished === true ||
    v2Payload?.is_finished === 'true' ||
    v2Payload?.is_finished === 1

  // Case-insensitive check for tier2 in course name
  const isTier2 = v2Payload?.course_name?.toLowerCase()?.includes('tier2')

  const normalizedCourseName = normalizeLmsCourseName(v2Payload?.course_name)
  const lookupKeys = getLmsCourseTreeLookupKeys(v2Payload, program)

  // Create the full v1.5 payload
  const v1dot5Payload = {
    // this is kind of backwards but add this in so the reverse mapping works in the K8DigitalProgramCardV2 component:
    contentfulID: programContentfulMapping?.find(
      entry =>
        entry?.courseName?.toLowerCase() ===
        normalizedCourseName?.toLowerCase(),
    )?.id,
    siteId: v2Payload?.site_id || null,
    totalLessons: countTotalLessons(v2Payload, courseManager, lookupKeys),
    completeLessons: countCompleteLessons(v2Payload),
    title:
      v2Payload?.class_title ||
      `Grade ${(
        normalizedCourseName || v2Payload?.course_name
      )?.toUpperCase()}`,
    // NOTE: this is misleading because registration is whole legacy property that no longer exists
    // but keep the prop name unchanged for now so the ClassCard component doesn't need changes yet
    instance: v2Payload?.registration_id
      ? String(v2Payload?.registration_id)
      : '',
    tier: isTier2 ? 2 : 1,
    is_finished: isFinished,
    course_name: v2Payload?.course_name || null,
    lesson_nodes: v2Payload?.lesson_nodes || null,
    user_id: v2Payload?.user_id ?? null,
    secondary_user_ids: v2Payload?.secondary_user_ids || [],
  }

  return v1dot5Payload
}

export function isCurrentSiteElementary(v2Payload, userContextManager) {
  if (!userContextManager?.elementaryActiveSiteId) {
    // don't filter at all if there's no active site
    return true
  }
  return v2Payload?.site_id === userContextManager?.elementaryActiveSiteId
}

/**
 * Parses the grade and program type from a router path
 * @param {string} path - The router path to parse
 * @returns {Object} - Object containing grade and program type
 */
export function parseGradeAndProgram(path) {
  try {
    if (!path || typeof path !== 'string') {
      return { grade: null, program: null }
    }

    const elementary = 'elementary'
    const dashboardElementary = 'grades-k-5'
    const isElementary =
      path.includes(elementary) || path.includes(dashboardElementary)

    const dashboardMiddleSchool = 'grades-6-8'
    const middleSchool = 'middle-school'
    const isMiddleSchool =
      path.includes(middleSchool) || path.includes(dashboardMiddleSchool)

    const digitalWellBeing = 'digital-well-being'
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    const isDigitalWellBeing =
      normalizedPath.startsWith(`/product/${digitalWellBeing}`) ||
      normalizedPath.startsWith(`/${digitalWellBeing}`)

    const gradesTier2kTo2 = 'grades-k-2'
    const gradesTier23To5 = 'grades-3-5'
    const gradesTier26To8 = 'grades-6-8'

    const isTier2 = path.includes('tier-2')
    const isTier2kTo2 = path.includes(gradesTier2kTo2)
    const isTier23To5 = path.includes(gradesTier23To5)
    const isTier26To8 = path.includes(gradesTier26To8)

    let program = null
    let programKey = null
    let grade = null

    if (isElementary) {
      program = programKeyMap[elementary]
      programKey = elementary
    }
    if (isMiddleSchool) {
      program = programKeyMap[middleSchool]
      programKey = middleSchool
    }
    if (isDigitalWellBeing) {
      program = programKeyMap[digitalWellBeing]
      programKey = digitalWellBeing
    }

    if (isTier2) {
      if (isTier2kTo2) {
        grade = GRADE_K2_TIERED_INTERVENTIONS
      }
      if (isTier23To5) {
        grade = GRADE_35_TIERED_INTERVENTIONS
      }
      if (isTier26To8) {
        grade = GRADE_68_TIERED_INTERVENTIONS
      }
      // Regular k8 grade parsing from the urls if not tier2
    } else {
      const gradeMatch = path.match(/lessons\/(preview|teach)\/grade-([k\d]+)/)
      if (gradeMatch) {
        grade = gradeMatch[2]
      }
    }

    return { grade, program, programKey }
  } catch (e) {
    console.error('Error parsing grade and program', e)
    return { grade: null, program: null }
  }
}

/**
 * Maps courses to their new Contentful IDs based on program type
 * @param {Object} params - Parameters object
 * @param {string} params.programType - The type of program (e.g., 'K5SelDigital', 'MspDigital')
 * @returns {Array} Array of courses with updated Contentful IDs
 */
export function coursesWithNewContentfulIds({ programType }) {
  let programMappings = null
  if (programType === programKeyMap['elementary']) {
    programMappings = elementaryV2ContentfulMappings
  }

  if (programType === programKeyMap['middle-school']) {
    programMappings = middleSchoolV2ContentfulMappings
  }

  if (programType === programKeyMap['digital-well-being']) {
    programMappings = digitalWellBeingV2ContentfulMappings
  }

  if (!programMappings) {
    console.warn(`No program mappings found for program type: ${programType}`)
    return []
  } else {
    return programMappings.map(course => {
      return { ...course, id: course.id }
    })
  }
}

/**
 * Converts course objects to link objects with slugs
 * @param {Array} courses - Array of course objects with displayTitle and id properties
 * @param {string} [courses[].slug] - Optional explicit slug. If provided, it will be used; otherwise slug is derived from displayTitle
 * @returns {Array} Array of link objects with slug, title, and entryId properties
 */
export function getCourseLinks(courses) {
  return courses.map(course => ({
    slug: course.slug || getSlug(course.displayTitle),
    title: course.displayTitle,
    entryId: course.id,
    shouldShowOnLessonsPage: course.shouldShowOnLessonsPage,
    ...(course.defaultLessonSlug != null && {
      defaultLessonSlug: course.defaultLessonSlug,
    }),
  }))
}

export function getClassId(location) {
  return extractClassIdFromSearch(location?.search || '')
}

export function getClass(classes, classId) {
  if (!classes || !classId) {
    return
  }
  let ourClass = classes?.find(
    c => parseInt(c?.registration_id) === parseInt(classId),
  )

  // backwards compatability for v1 lms usage in K8teach for now
  if (!ourClass) {
    ourClass = classes?.find(c => parseInt(c?.instance) === parseInt(classId))
  }

  return ourClass
}

/**
 * Selects and returns the current class instance data along with all available classes
 *
 * This function combines the class selection logic by:
 * 1. Getting all classes for the current course using selectAllClassesInCurrentCourseData
 * 2. Extracting the class ID from the location search parameters
 * 3. Finding the specific class instance using getClass
 */
export function selectCurrentClassInstanceData({
  instructorData,
  courseManager,
  match,
  userContext,
  location,
}) {
  const defaultReturn = {
    ourClass: null,
    classId: null,
  }
  try {
    if (
      !instructorData ||
      !courseManager ||
      !match ||
      !userContext ||
      !location
    ) {
      return defaultReturn
    }

    const classes = selectAllClassesInCurrentCourseData({
      instructorData,
      courseManager,
      match,
      userContext,
    })

    const classId = getClassId(location)
    const ourClass = getClass(classes, classId)

    if (!classId || !ourClass) {
      return defaultReturn
    }

    return {
      ourClass,
      classId,
    }
  } catch (error) {
    console.error('Error selecting current class instance data:', error)
    return defaultReturn
  }
}
